"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { safeSignOutLocalClient } from "@/lib/auth/safe-sign-out-client";
import { toast } from "react-hot-toast";
import { useAuth } from "@/hooks/use-auth";
import { useNotificationSound } from "@/hooks/useNotificationSound";
import { apiFetch } from "@/lib/api";
import { fetchNotificationsUnreadMeta } from "@/lib/notifications/notification-rpc";

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: "booking" | "message" | "payment" | "listing" | "system" | "review" | "favorite";
  related_id?: string | null;
  action_url?: string | null;
  is_read: boolean;
  created_at: string;
  metadata?: Record<string, unknown>;
  data?: { action_url?: string; [key: string]: unknown };
}

interface NotificationContextValue {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAllReadLoading: boolean;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refetch: () => Promise<void>;
  markViewed: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [markAllReadLoading, setMarkAllReadLoading] = useState(false);
  const { user } = useAuth();
  const router = useRouter();
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>["channel"]> | null>(null);
  const fetchInProgressRef = useRef(false);
  const markViewedInProgressRef = useRef(false);

  const handleUnauthorized = useCallback(() => {
    // Skip "Session expired" toast when user explicitly logged out (only show "Logged out successfully")
    if (typeof window !== "undefined" && sessionStorage.getItem("explicit_logout") === "1") {
      sessionStorage.removeItem("explicit_logout");
      router.push("/");
      return;
    }
    void (async () => {
      const supabase = createClient();
      await safeSignOutLocalClient(supabase);
      try {
        await fetch("/api/auth/clear-session", { method: "POST", credentials: "same-origin" });
      } catch {
        /* ignore */
      }
      router.push("/login");
    })();
  }, [router]);

  const fetchUnreadCount = useCallback(async (): Promise<number> => {
    if (!user?.id) return 0;
    try {
      const { unreadCount } = await fetchNotificationsUnreadMeta();
      return unreadCount ?? 0;
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 401) handleUnauthorized();
    }
    return 0;
  }, [user?.id, handleUnauthorized]);

  const refetch = useCallback(async () => {
    if (!user?.id) return;
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;

    try {
      setLoading(true);
      const data = await apiFetch<{ notifications: Notification[] }>("/api/notifications");
      setNotifications(data.notifications || []);
      const count = await fetchUnreadCount();
      setUnreadCount(count);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        handleUnauthorized();
        return;
      }
      console.error("Error refetching notifications:", err);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [user?.id, handleUnauthorized, fetchUnreadCount]);

  const fetchInitial = useCallback(async () => {
    if (!user?.id) return;
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;

    try {
      setLoading(true);
      const supabase = createClient();

      const [notificationsResult, count] = await Promise.all([
        supabase
          .from("app_notifications")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false }),
        fetchUnreadCount(),
      ]);

      const { data: notificationsData, error } = notificationsResult;
      if (error) {
        console.error("Error fetching notifications:", error);
        return;
      }

      setNotifications(notificationsData || []);
      setUnreadCount(count);
    } catch (error) {
      console.error("Error in fetchNotifications:", error);
    } finally {
      setLoading(false);
      fetchInProgressRef.current = false;
    }
  }, [user?.id, fetchUnreadCount]);

  // Initial fetch (once per user)
  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }
    fetchInitial();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch unread count when user returns to tab (debounced)
  useEffect(() => {
    if (!user?.id) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        fetchUnreadCount().then((count) => setUnreadCount(count));
      }, 200);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user?.id, fetchUnreadCount]);

  // Periodic poll when tab is visible (fallback when Realtime doesn't deliver)
  useEffect(() => {
    if (!user?.id) return;

    const POLL_INTERVAL_MS = 15_000;
    let lastPoll = 0;

    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastPoll < POLL_INTERVAL_MS - 500) return; // Prevent overlap
      lastPoll = now;
      fetchUnreadCount().then((count) => setUnreadCount(count));
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [user?.id, fetchUnreadCount]);

  // Supabase Realtime subscription
  useEffect(() => {
    if (!user?.id) return;

    const supabase = createClient();
    const currentUserId = user.id;

    const channel = supabase
      .channel(`app_notifications:${currentUserId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "app_notifications",
        },
        (payload) => {
          const newRow = payload.new as Record<string, unknown>;
          if (!newRow?.id || (newRow.user_id as string) !== currentUserId) return;

          const notification: Notification = {
            id: newRow.id as string,
            title: (newRow.title as string) ?? "",
            message: (newRow.message as string) ?? "",
            type: (newRow.type as Notification["type"]) ?? "system",
            related_id: (newRow.related_id as string | null) ?? null,
            action_url: (newRow.action_url as string | null) ?? null,
            is_read: (newRow.is_read as boolean) ?? false,
            created_at: (newRow.created_at as string) ?? new Date().toISOString(),
            metadata: (newRow.data as Record<string, unknown>) ?? undefined,
            data: (newRow.data as Notification["data"]) ?? undefined,
          };

          setNotifications((prev) => [notification, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "app_notifications",
        },
        (payload) => {
          const updated = payload.new as Record<string, unknown>;
          if (!updated?.id || (updated.user_id as string) !== currentUserId) return;

          setNotifications((prev) =>
            prev.map((n) =>
              n.id === updated.id
                ? {
                    ...n,
                    is_read: (updated.is_read as boolean) ?? n.is_read,
                    updated_at: (updated.updated_at as string) ?? n.created_at,
                  }
                : n
            )
          );

          if ((updated.is_read as boolean) === true) {
            setUnreadCount((prev) => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.warn("[NotificationContext] Realtime channel error - using poll fallback");
        }
      });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [user?.id]);

  const markAsRead = useCallback(
    async (notificationId: string) => {
      if (!user?.id) return;

      try {
        const supabase = createClient();
        const now = new Date().toISOString();
        const { error } = await (supabase as any)
          .from("app_notifications")
          .update({ is_read: true, read_at: now })
          .eq("id", notificationId)
          .eq("user_id", user.id);

        if (error) {
          console.error("Error marking notification as read:", error);
          return;
        }

        setNotifications((prev) =>
          prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Error in markAsRead:", error);
      }
    },
    [user?.id]
  );

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    setMarkAllReadLoading(true);

    try {
      const data = await apiFetch<{ updatedCount: number }>("/api/notifications/mark-all-read", {
        method: "POST",
      });
      const updatedCount = data.updatedCount ?? 0;

      // Optimistic UI: immediately update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);

      if (updatedCount > 0) {
        toast.success("All notifications marked as read");
        await apiFetch("/api/notifications/mark-viewed", { method: "POST" });
      }
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        handleUnauthorized();
        return;
      }
      console.error("Error in markAllAsRead:", err);
      toast.error("Failed to mark all as read");
    } finally {
      setMarkAllReadLoading(false);
    }
  }, [user?.id, handleUnauthorized]);

  const markViewed = useCallback(async () => {
    if (markViewedInProgressRef.current) return;
    markViewedInProgressRef.current = true;

    try {
      await apiFetch("/api/notifications/mark-viewed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      setUnreadCount(0);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 401) handleUnauthorized();
      else console.log("Failed to mark notifications as viewed:", err);
    } finally {
      markViewedInProgressRef.current = false;
    }
  }, [handleUnauthorized]);

  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        const supabase = createClient();
        const { error } = await supabase
          .from("app_notifications")
          .delete()
          .eq("id", id)
          .eq("user_id", user?.id ?? "");

        if (error) {
          console.log("Error deleting notification:", error);
          toast.error("Failed to delete notification");
          return;
        }
        toast.success("Notification deleted");
        await refetch();
      } catch (error) {
        console.log("Error deleting notification:", error);
        toast.error("Failed to delete notification");
      }
    },
    [refetch, user?.id]
  );

  useNotificationSound(unreadCount);

  const value: NotificationContextValue = {
    notifications,
    unreadCount,
    loading,
    markAllReadLoading,
    markAsRead,
    markAllAsRead,
    refetch,
    markViewed,
    deleteNotification,
  };

  return (
    <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return ctx;
}
